import AyudaCasos from "@/components/ayuda/AyudaCasos";
import AyudaCorreos from "@/components/ayuda/AyudaCorreos";
import AyudaGuias from "@/components/ayuda/AyudaGuias";
import AyudaNotificaciones from "@/components/ayuda/AyudaNotificaciones";
import AyudaAsesores from "@/components/ayuda/AyudaAsesores";
import AyudaPanelConfig from "@/components/ayuda/AyudaPanelConfig";
import AyudaVentas from "@/components/ayuda/AyudaVentas";
import { Section } from "@/components/ayuda/comunes";
import { isAdmin, useAuth } from "@/lib/auth";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Índice del centro de ayuda: cada entrada ancla a una Section con ese id.
const INDICE: { href: string; label: string }[] = [
  { href: "#primeros-pasos", label: "Primeros pasos" },
  { href: "#casos", label: "Casos" },
  { href: "#detalle", label: "Dentro del caso" },
  { href: "#correo-entrada", label: "Correos" },
  { href: "#proveedores", label: "Seguimiento a proveedores" },
  { href: "#guias", label: "Guías" },
  { href: "#ventas", label: "Ventas" },
  { href: "#recordatorios", label: "Recordatorios" },
  { href: "#notificaciones", label: "Notificaciones" },
  { href: "#instalar", label: "Instalar en el celular" },
  { href: "#panel", label: "Panel y estadísticas" },
  { href: "#configuracion", label: "Configuración" },
  { href: "#asesores", label: "Asesores comerciales" },
  { href: "#atajos", label: "Atajos" },
];

export default function Ayuda() {
  const user = useAuth((s) => s.user);
  // El botón «Ayuda» de cada vista abre `/ayuda#<sección>` en otra pestaña. En
  // una carga nueva el navegador busca el ancla ANTES de que React pinte el
  // manual y no la encuentra: se salta aquí, ya con las secciones montadas.
  // Y no basta un salto: las capturas de las secciones de ARRIBA cargan después,
  // crecen y empujan la buscada hacia abajo (medido: caía dos secciones antes).
  // Mientras la página cambie de alto se vuelve a anclar, hasta que la persona
  // toque la página o pasen 4 s.
  // Se observa el contenedor del manual, NO `document.body`: `html`, `body` y
  // `#root` llevan `height: 100%` (index.css) y no crecen con el contenido.
  const { hash } = useLocation();
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hash || !raiz.current) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!el) return;
    const anclar = () => el.scrollIntoView({ block: "start" });
    anclar();
    const ro = new ResizeObserver(anclar);
    ro.observe(raiz.current);
    const soltar = () => ro.disconnect();
    const eventos = ["wheel", "touchstart", "keydown", "mousedown"] as const;
    eventos.forEach((ev) => window.addEventListener(ev, soltar, { once: true, passive: true }));
    const t = window.setTimeout(soltar, 4000);
    return () => {
      soltar();
      window.clearTimeout(t);
      eventos.forEach((ev) => window.removeEventListener(ev, soltar));
    };
  }, [hash]);
  return (
    <div ref={raiz} className="max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Centro de ayuda</h1>
        <p className="text-sm text-zinc-400 mt-1">
          Cómo funciona ArkTickets y cómo sacarle jugo, con pantallas reales.
          Si algo no está aquí, pregunta al administrador.
          {isAdmin(user) && (
            <>
              {" "}La documentación técnica del API (solo admins) vive en
              <a className="text-accent-400 hover:underline ml-1" href="/api/docs" target="_blank" rel="noreferrer">
                /api/docs
              </a>
              .
            </>
          )}
        </p>
        <nav className="mt-3 flex flex-wrap gap-1.5">
          {INDICE.map((i) => (
            <a
              key={i.href}
              href={i.href}
              className="text-xs px-2 py-1 rounded border border-ink-700 text-zinc-300 hover:border-accent-500/50 hover:text-accent-300 transition"
            >
              {i.label}
            </a>
          ))}
        </nav>
      </div>

      <Section id="primeros-pasos" title="Primeros pasos">
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Roles</strong>: <strong>admin</strong> administra todo;{" "}
            <strong>soporte</strong> crea y opera casos, correos y guías;{" "}
            <strong>usuario</strong> solo consulta.
          </li>
          <li>
            <strong>Navegación</strong>: Panel (resumen y estadísticas), Casos,
            Bandeja (correos sin caso), Alertas, Guías y Enlaces. En el celular
            la barra va abajo y el resto vive en "Más".
          </li>
          <li>
            <strong>Botón «Ayuda» de cada pantalla</strong>: junto al título de cada
            pantalla, pestaña del caso y ventana de trabajo grande. Explica qué hace cada
            botón —y por qué a veces sale gris— y qué significa cada ficha o color. La app no
            usa globitos al pasar el mouse: en el celular no existen.
          </li>
          <li>
            <strong>Tu Perfil</strong> es la base: configura tu correo
            corporativo (para enviar a clientes y recibir tu resumen diario),
            activa las notificaciones del navegador y vincula tu Telegram.
          </li>
          <li>
            La app se puede <a className="text-accent-400 hover:underline" href="#instalar">instalar en el celular</a> como
            cualquier app.
          </li>
        </ul>
      </Section>

      <AyudaCasos />
      <AyudaCorreos />
      <AyudaGuias />
      <AyudaVentas esAdmin={isAdmin(user)} />
      <AyudaNotificaciones />
      <AyudaPanelConfig />
      <AyudaAsesores />
    </div>
  );
}
